import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  FileText, Image as ImageIcon, FileVideo, Upload, Search,
  Download, Pencil, Trash2, Plus, Loader2, X, AlertCircle, CheckCircle2,
  ShieldCheck, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtRelative } from '../lib/constants';
import { useAuth } from '../lib/auth-context';
import { logger } from '../lib/logger';

const fileIcon = (ct = '', size = 'w-6 h-6') => {
  if (ct.startsWith('image/')) return <ImageIcon className={`${size} text-blue-600`} />;
  if (ct.startsWith('video/')) return <FileVideo className={`${size} text-purple-600`} />;
  return <FileText className={`${size} text-[hsl(var(--primary))]`} />;
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DocumentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterResort, setFilterResort] = useState('all');
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null); // doc being edited
  const [deleting, setDeleting] = useState(null); // doc being deleted

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterCategory && filterCategory !== 'all') params.set('category', filterCategory);
    if (filterResort && filterResort !== 'all') params.set('resort_slug', filterResort);
    if (search.trim()) params.set('q', search.trim());
    try {
      const r = await api.get(`/documents${params.toString() ? `?${params}` : ''}`);
      setDocs(r.data || []);
    } catch (err) {
      logger.error('DocumentsPage: load failed', err);
      toast.error(t('documentsPage.loadError', 'Errore caricamento documenti'));
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterResort, search]);

  useEffect(() => {
    api
      .get('/documents/categories')
      .then((r) => setConfig(r.data))
      .catch((err) => {
        logger.error('DocumentsPage: failed to load categories', err);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      // Trigger browser download
      const blob = new Blob([r.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || `${doc.name}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Maybe it's an external URL doc
      if (doc.url) {
        window.open(doc.url, '_blank');
      } else {
        toast.error(t('documentsPage.downloadUnavailable', 'Download non disponibile'));
      }
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/documents/${deleting.id}`);
      toast.success(t('documentsPage.removed', 'Documento rimosso'));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('documentsPage.deleteError', 'Errore eliminazione'));
    }
  };

  const categoryById = (id) => config?.categories?.find((c) => c.id === id) || { label: id };
  const resortById = (id) => config?.resorts?.find((r) => r.id === id) || { label: id };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl">{t('documentsPage.title', 'Vault Documenti')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('documentsPage.subtitle', 'Brochure, project financing, contratti, KYC, progress · Storage privato')}
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="upload-document-btn">
          <Upload className="w-4 h-4 mr-2" /> {t('documentsPage.upload', 'Carica documento')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('documentsPage.searchPlaceholder', 'Cerca per nome o descrizione…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="docs-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger data-testid="docs-filter-category"><SelectValue placeholder={t('documentsPage.category', 'Categoria')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('documentsPage.allCategories', 'Tutte le categorie')}</SelectItem>
              {(config?.categories || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterResort} onValueChange={setFilterResort}>
            <SelectTrigger data-testid="docs-filter-resort"><SelectValue placeholder="Resort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('documentsPage.allResorts', 'Tutti i resort')}</SelectItem>
              {(config?.resorts || []).map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Documents grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('documentsPage.loading', 'Caricamento…')}
        </div>
      ) : docs.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="docs-empty">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('documentsPage.emptyFiltered', 'Nessun documento trovato per i filtri attuali.')}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="docs-grid">
          {docs.map((d) => (
            <Card key={d.id} className="hover:shadow-md transition-shadow" data-testid={`doc-card-${d.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                      {fileIcon(d.content_type, 'w-5 h-5')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate text-sm">{d.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {fmtBytes(d.size)} · {fmtRelative(d.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                  <Badge variant="secondary" className="text-[10px]">
                    {categoryById(d.category).label}
                  </Badge>
                  {d.resort_slug && (
                    <Badge variant="outline" className="text-[10px]">
                      <MapPin className="w-2.5 h-2.5 mr-0.5" /> {resortById(d.resort_slug).label}
                    </Badge>
                  )}
                  {d.lead_visible && (
                    <Badge className="text-[10px] bg-amber-500 text-white">
                      {t('documentsPage.leadVisible', 'Visibile lead')}
                    </Badge>
                  )}
                </div>
                {d.description && (
                  <p className="text-xs text-muted-foreground mt-2.5 line-clamp-2">{d.description}</p>
                )}
                <div className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  {(d.access_roles || []).join(', ')}
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <Button
                    size="sm" variant="outline" className="flex-1"
                    onClick={() => handleDownload(d)}
                    data-testid={`doc-download-${d.id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> {t('documentsPage.download', 'Scarica')}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setEditing(d)}
                    data-testid={`doc-edit-${d.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'amministrativo') && (
                    <Button
                      size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleting(d)}
                      data-testid={`doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        config={config}
        onUploaded={() => { setUploadOpen(false); load(); }}
      />
      <EditDialog
        doc={editing}
        config={config}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documentsPage.deleteConfirmTitle', 'Eliminare il documento?')}</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleting?.name}&quot; {t('documentsPage.deleteConfirmDesc', 'verrà rimosso dal vault. Lo storage rimane (soft delete).')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">{t('documentsPage.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-ok">
              {t('documentsPage.delete', 'Elimina')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function UploadDialog({ open, onClose, config, onUploaded }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('brochure');
  const [resort, setResort] = useState('');
  const [description, setDescription] = useState('');
  const [leadVisible, setLeadVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setName('');
      setCategory('brochure');
      setResort('');
      setDescription('');
      setLeadVisible(false);
    }
  }, [open]);

  const onSelect = (f) => {
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error(t('documentsPage.fileTooLarge', 'Il file supera 50 MB'));
      return;
    }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^/.]+$/, ''));
  };

  const submit = async () => {
    if (!file) { toast.error(t('documentsPage.selectFile', 'Seleziona un file')); return; }
    if (!name.trim()) { toast.error(t('documentsPage.enterName', 'Inserisci un nome')); return; }
    const allowed = config?.upload_roles_per_category?.[category] || ['admin'];
    if (user?.role !== 'admin' && !allowed.includes(user?.role)) {
      toast.error(t('documentsPage.roleCannotUpload', { defaultValue: 'Il tuo ruolo ({{role}}) non può caricare in "{{category}}"', role: user?.role, category }));
      return;
    }
    setSubmitting(true);
    const form = new FormData();
    form.append('file', file);
    form.append('name', name.trim());
    form.append('category', category);
    if (resort) form.append('resort_slug', resort);
    if (description) form.append('description', description);
    form.append('lead_visible', leadVisible ? 'true' : 'false');
    try {
      await api.post('/documents/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('documentsPage.uploaded', 'Documento caricato'));
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('documentsPage.uploadError', 'Errore upload'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>{t('documentsPage.upload', 'Carica documento')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Drag & drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onSelect(f);
            }}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              drag ? 'border-[var(--gp-gold)] bg-[var(--gp-gold)]/10' : 'border-border hover:border-[var(--gp-gold)]/50'
            }`}
            data-testid="upload-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {fileIcon(file.type, 'w-5 h-5')}
                  <span className="text-sm truncate">{file.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{fmtBytes(file.size)}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setFile(null)}><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">{t('documentsPage.dragDrop', 'Trascina qui un file o clicca')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('documentsPage.dragDropHint', 'PDF, immagini, video · max 50MB')}</p>
                <input
                  type="file" className="hidden"
                  accept=".pdf,image/*,video/mp4,video/quicktime,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => onSelect(e.target.files?.[0])}
                  data-testid="upload-file-input"
                />
              </label>
            )}
          </div>
          <Input placeholder={t('documentsPage.docName', 'Nome documento')} value={name} onChange={(e) => setName(e.target.value)} data-testid="upload-name" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(config?.categories || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resort || '_none'} onValueChange={(v) => setResort(v === '_none' ? '' : v)}>
              <SelectTrigger data-testid="upload-resort"><SelectValue placeholder={t('documentsPage.resortOptional', 'Resort (opzionale)')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">{t('documentsPage.commonToAll', '— Comune a tutti —')}</SelectItem>
                {(config?.resorts || []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder={t('documentsPage.descOptional', 'Descrizione (opzionale)')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            data-testid="upload-description"
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={leadVisible} onCheckedChange={setLeadVisible} data-testid="upload-lead-visible" />
            {t('documentsPage.leadVisibleLabel', 'Visibile ai lead qualificati sul sito pubblico')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('documentsPage.cancel', 'Annulla')}</Button>
          <Button onClick={submit} disabled={submitting || !file || !name.trim()} data-testid="upload-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {t('documentsPage.uploadBtn', 'Carica')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditDialog({ doc, config, onClose, onSaved }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [resort, setResort] = useState('');
  const [description, setDescription] = useState('');
  const [leadVisible, setLeadVisible] = useState(false);
  const [accessRoles, setAccessRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setName(doc.name || '');
    setCategory(doc.category || 'brochure');
    setResort(doc.resort_slug || '');
    setDescription(doc.description || '');
    setLeadVisible(!!doc.lead_visible);
    setAccessRoles(doc.access_roles || []);
  }, [doc]);

  if (!doc) return null;

  const toggleRole = (role) => {
    setAccessRoles((prev) => prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/documents/${doc.id}`, {
        name: name.trim(),
        category,
        resort_slug: resort || null,
        description: description || null,
        access_roles: accessRoles,
        lead_visible: leadVisible,
      });
      toast.success(t('documentsPage.updated', 'Documento aggiornato'));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('documentsPage.updateError', 'Errore aggiornamento'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-dialog">
        <DialogHeader><DialogTitle>{t('documentsPage.editDoc', 'Modifica documento')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(config?.categories || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resort || '_none'} onValueChange={(v) => setResort(v === '_none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Resort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">{t('documentsPage.commonToAll', '— Comune a tutti —')}</SelectItem>
                {(config?.resorts || []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder={t('documentsPage.description', 'Descrizione')} />
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">{t('documentsPage.rolesCanSee', 'Ruoli che possono vedere')}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(config?.roles || []).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    accessRoles.includes(r)
                      ? 'bg-[hsl(var(--primary))] text-white'
                      : 'bg-[var(--surface-2)] text-muted-foreground hover:bg-border'
                  }`}
                  data-testid={`edit-role-${r}`}
                >
                  {accessRoles.includes(r) && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                  {r}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={leadVisible} onCheckedChange={setLeadVisible} />
            {t('documentsPage.leadVisibleLabel', 'Visibile ai lead qualificati sul sito pubblico')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('documentsPage.cancel', 'Annulla')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="edit-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            {t('documentsPage.save', 'Salva')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
